import React, { useEffect, useMemo, useState } from 'react';
import { withStyles } from '@material-ui/core';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Switch from '@material-ui/core/Switch';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';

import AdminMenu from '../AdminMenu';
import DataService from '../../../services/DataService';
import { useNotificationContext } from '../../../services/NotificationService';
import styles from './styles';

const PAGE_SIZE = 100;

const Users = (props) => {
    const { classes } = props;
    const notifications = useNotificationContext();

    const [users, setUsers] = useState([]);
    const [plansConfig, setPlansConfig] = useState({ default_plan: null, plans: [] });
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [savingUserId, setSavingUserId] = useState(null);

    const planOptions = useMemo(() => (plansConfig.plans || []).filter((plan) => plan && plan.id), [plansConfig]);
    const planLabelLookup = useMemo(() => {
        const lookup = {};
        planOptions.forEach((plan) => {
            if (plan && plan.id) {
                lookup[plan.id] = plan.label || plan.id;
            }
        });
        return lookup;
    }, [planOptions]);

    const loadPlans = () => {
        setLoadingPlans(true);
        DataService.GetUserAccessPlans()
            .then((response) => {
                setPlansConfig(response.data || { default_plan: null, plans: [] });
            })
            .catch((error) => {
                notifications.dispatch({
                    type: 'DISPLAY_ALERT',
                    payload: {
                        message: error?.response?.data?.detail || 'Unable to load plan configuration.',
                        severity: 'error'
                    }
                });
            })
            .finally(() => setLoadingPlans(false));
    };

    const loadUsers = () => {
        setLoadingUsers(true);
        DataService.GetUsers(0, PAGE_SIZE)
            .then((response) => {
                setUsers(response.data || []);
            })
            .catch((error) => {
                notifications.dispatch({
                    type: 'DISPLAY_ALERT',
                    payload: {
                        message: error?.response?.data?.detail || 'Unable to load users.',
                        severity: 'error'
                    }
                });
            })
            .finally(() => setLoadingUsers(false));
    };

    useEffect(() => {
        loadPlans();
        loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const extractErrorMessage = (error) => {
        const detail = error?.response?.data?.detail;
        if (!detail) {
            return 'Unable to update user access.';
        }
        if (typeof detail === 'string') {
            return detail;
        }
        if (Array.isArray(detail)) {
            return detail.map((item) => item?.msg || '').filter(Boolean).join('; ') || 'Unable to update user access.';
        }
        if (typeof detail === 'object') {
            return detail.msg || JSON.stringify(detail);
        }
        return 'Unable to update user access.';
    };

    const handleUpdateAccess = (user, updates) => {
        if (!user) {
            return;
        }
        const payload = {
            is_admin: updates.is_admin,
            payment_plan: updates.payment_plan,
        };
        setSavingUserId(user.id);
        DataService.UpdateUserAccess(user.id, payload)
            .then((response) => {
                const updatedProfile = response.data;
                setUsers((prev) => prev.map((item) => (item.id === user.id ? { ...item, ...updatedProfile } : item)));
                notifications.dispatch({
                    type: 'DISPLAY_ALERT',
                    payload: {
                        message: 'User access updated successfully.',
                        severity: 'success'
                    }
                });
            })
            .catch((error) => {
                const message = extractErrorMessage(error);
                notifications.dispatch({
                    type: 'DISPLAY_ALERT',
                    payload: {
                        message,
                        severity: 'error'
                    }
                });
                loadUsers();
            })
            .finally(() => setSavingUserId(null));
    };

    const handlePlanChange = (user, nextPlanId) => {
        if (!user || !nextPlanId) {
            return;
        }
        const currentPlanId = user.payment_plan || plansConfig.default_plan;
        if (nextPlanId === currentPlanId) {
            return;
        }

        handleUpdateAccess(user, {
            is_admin: user.account_type === 'admin',
            payment_plan: nextPlanId,
        });
    };

    const handleAdminToggle = (user, checked) => {
        if (!user) {
            return;
        }
        if ((user.account_type === 'admin') === checked) {
            return;
        }
        handleUpdateAccess(user, {
            is_admin: checked,
            payment_plan: user.payment_plan || plansConfig.default_plan,
        });
    };

    const isLoading = loadingUsers || loadingPlans;

    return (
        <div className={classes.root}>
            <main className={classes.main}>
                <Container maxWidth={false}>
                    <Grid container spacing={2}>
                        <Grid item sm={3}>
                            <AdminMenu page="users" />
                        </Grid>
                        <Grid item sm={9}>
                            <div className={classes.spacer}></div>
                            <Typography variant="h6" gutterBottom>
                                Users
                            </Typography>
                            <Divider />
                            {(isLoading) ? (
                                <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <TableContainer component={Paper} className={classes.tableWrapper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className={classes.tableHeadCell}>Username</TableCell>
                                                <TableCell className={classes.tableHeadCell}>Email</TableCell>
                                                <TableCell className={classes.tableHeadCell}>Payment Plan</TableCell>
                                                <TableCell className={classes.tableHeadCell}>Admin</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {users.map((user) => {
                                                const planId = user.payment_plan || plansConfig.default_plan;
                                                const planLabel = planLabelLookup[planId] || planId || 'Unknown';
                                                const isAdmin = user.account_type === 'admin';
                                                const disabled = savingUserId === user.id;

                                                return (
                                                    <TableRow key={user.id}>
                                                        <TableCell>
                                                            {user.username}
                                                            {isAdmin && (
                                                                <Chip
                                                                    size="small"
                                                                    color="primary"
                                                                    label="Admin"
                                                                    className={classes.statusChip}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>{user.email || '—'}</TableCell>
                                                        <TableCell>
                                                            <FormControl className={classes.selectControl} size="small">
                                                                <Select
                                                                    value={planId || ''}
                                                                    onChange={(event) => handlePlanChange(user, event.target.value)}
                                                                    disabled={disabled}
                                                                >
                                                                    <MenuItem value="" disabled>
                                                                        Select plan
                                                                    </MenuItem>
                                                                    {planId && !planLabelLookup[planId] && (
                                                                        <MenuItem value={planId}>{planLabel}</MenuItem>
                                                                    )}
                                                                    {planOptions.map((plan) => (
                                                                        <MenuItem key={plan.id} value={plan.id}>
                                                                            {plan.label || plan.id}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Switch
                                                                checked={isAdmin}
                                                                onChange={(event) => handleAdminToggle(user, event.target.checked)}
                                                                color="primary"
                                                                disabled={disabled}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Grid>
                    </Grid>
                </Container>
            </main>
        </div>
    );
};

export default withStyles(styles)(Users);
